// Simplified Report Generation Hook
// A more reliable approach that doesn't depend on complex async polling

import { useState, useCallback, useRef, useEffect } from 'react';

const useSimpleReportGeneration = () => {
  // State management
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [reportReady, setReportReady] = useState(false);
  
  // Refs for cleanup
  const abortControllerRef = useRef(null);
  const progressIntervalRef = useRef(null);

  // Simulate progress for better UX
  const simulateProgress = useCallback(() => {
    let currentProgress = 0;
    
    // Clear any existing interval
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    
    progressIntervalRef.current = setInterval(() => {
      currentProgress += Math.random() * 15;
      if (currentProgress > 90) {
        currentProgress = 90; // Cap at 90% until real completion
      }
      setProgress(Math.floor(currentProgress));
    }, 1000);
  }, []);

  // Stop progress simulation
  const stopProgressSimulation = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  // Generate report with direct API call
  const generateReport = useCallback(async (planData) => {
    // Reset state
    setIsGenerating(true);
    setProgress(0);
    setSummary(null);
    setError(null);
    setReportReady(false);
    
    // Start progress simulation
    simulateProgress();
    
    // Create abort controller
    abortControllerRef.current = new AbortController();
    
    try {
      console.log('[SimpleReportGeneration] Generating report...');
      
      // Call the sophisticated report generation endpoint with timeout
      console.log('[SimpleReportGeneration] Calling sophisticated endpoint...');
      
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error('Request timeout after 90 seconds. The AI is taking longer than expected to generate your report. Please try again or use the fallback option.'));
        }, 90000); // 90 second timeout
      });
      
      // Race between fetch and timeout
      const fetchPromise = fetch('/api/generate-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(planData),
        signal: abortControllerRef.current.signal
      });
      
      const response = await Promise.race([fetchPromise, timeoutPromise]);
      
      // Check if response is ok
      if (!response.ok) {
        // Handle specific error types
        if (response.status === 504) {
          throw new Error('The AI service is taking longer than expected to generate your report. This sometimes happens with complex analyses. Please try again, or contact support if the issue persists.');
        }
        
        // Try to get error message from response
        let errorMessage = `Server error: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          // Ignore JSON parse error, use status-based message
          if (response.status === 500) {
            errorMessage = 'Internal server error. Please try again in a few moments.';
          } else if (response.status === 503) {
            errorMessage = 'Service temporarily unavailable. Please try again later.';
          }
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      
      if (data.success && data.summary) {
        console.log('[SimpleReportGeneration] Report generated successfully');
        
        // Stop progress simulation and set to 100%
        stopProgressSimulation();
        setProgress(100);
        
        // Set the summary
        setSummary(data.summary);
        setReportReady(true);
        setIsGenerating(false);
      } else {
        throw new Error(data.error || 'Failed to generate report');
      }
      
    } catch (err) {
      console.error('[SimpleReportGeneration] Error:', err);
      
      // Stop progress simulation
      stopProgressSimulation();
      
      if (err.name === 'AbortError') {
        console.log('[SimpleReportGeneration] Generation cancelled');
        setIsGenerating(false);
        setProgress(0);
      } else {
        // Check if this is a timeout or 504 error - offer fallback
        const isTimeoutError = err.message.includes('timeout') || err.message.includes('504');
        let errorMessage = err.message;
        
        if (isTimeoutError) {
          errorMessage += '\n\nTip: You can try generating a simpler report or contact support for assistance with complex analyses.';
        }
        
        // Set error and stop generation - let user know what happened
        setError(errorMessage);
        setIsGenerating(false);
        setProgress(0);
        setReportReady(false);
        setSummary(null);
      }
    }
  }, [simulateProgress, stopProgressSimulation]);


  // Cancel generation
  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    stopProgressSimulation();
    setIsGenerating(false);
    setProgress(0);
    setSummary(null);
    setError(null);
    setReportReady(false);
  }, [stopProgressSimulation]);

  // Reset everything
  const reset = useCallback(() => {
    cancel();
  }, [cancel]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      stopProgressSimulation();
    };
  }, [stopProgressSimulation]);

  // Estimated time (simpler calculation)
  const estimatedTime = isGenerating ? Math.max(30 - Math.floor(progress / 3), 5) : 0;

  return {
    // State
    isGenerating,
    progress,
    summary,
    error,
    reportReady,
    estimatedTime,
    
    // Actions
    generateReport,
    cancel,
    reset
  };
};

export default useSimpleReportGeneration;